<script setup lang="ts">
import { useDialogPluginComponent } from 'quasar';
import { toRef } from 'vue';
import { useForm } from 'vee-validate';
import * as yup from 'yup';

import type { Locales, SectionDto } from '@/services/types/api.types';
import { useLanguageValidationErrors } from '@/composables';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { localizableString } from '@/helpers/validation.helper';
import { useSectionsStore } from '@/stores';

import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import LocalizedInput from '@/components/LocalizedInput.vue';
import { provideEditLocale } from '@/composables/data-display-utils';

const sectionsStore = useSectionsStore();

// PROPS
const props = withDefaults(
  defineProps<{
    consultationId: number;
    section: SectionDto;
    languageEnabled: Locales[];
    isTemplate?: boolean;
  }>(),
  {
    isTemplate: false,
  },
);

// DIALOG SET-UP
defineEmits([...useDialogPluginComponent.emits]);
const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

// FORM
const { handleSubmit, errors, meta } = useForm({
  initialValues: props.section,
  validationSchema: yup.object<SectionDto>({
    title: localizableString(props.languageEnabled),
    description: localizableString(props.languageEnabled),
  }),
});
const languageErrors = useLanguageValidationErrors(errors);
const editLocale = provideEditLocale(toRef(() => props.languageEnabled));

const onSubmit = handleSubmit(async (values) => {
  await wrapSavingOperation(
    async () =>
      await sectionsStore.saveSection(values, {
        isTemplate: props.isTemplate,
        consultationId: props.consultationId,
      }),
    () => onDialogOK(),
  );
});
</script>
<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide" persistent>
    <q-card class="q-dialog-plugin" style="width: 600px">
      <q-card-section class="row">
        <div class="text-h6">{{ $t('section.dialogs.add.title') }}</div>
        <q-space />
        <language-switcher
          v-model="editLocale"
          :has-validation-errors="languageErrors"
          :language-enabled="languageEnabled"
        />
      </q-card-section>
      <q-separator />
      <q-card-section>
        <localized-input
          name="title"
          :label="$t('section.title')"
          :editLocale="editLocale"
          outlined
          :language_enabled="languageEnabled"
        />
        <localized-input
          name="description"
          :label="$t('section.description')"
          type="textarea"
          :editLocale="editLocale"
          outlined
          :language_enabled="languageEnabled"
        />
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn color="primary" flat :label="$t('global.cancel')" @click="onDialogCancel" />
        <q-btn color="primary" :label="$t('global.save')" @click="onSubmit" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
